import { useEffect, useMemo, useRef, useState } from 'react';

import { RunningExperiments, useExperiments } from 'state/experiments';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isMobile } from 'utils/environment';

/**
 * Depending on the experiment variant, the user will see the following:
 *
 * | Variant    | Mobile (mobile web)    | Web                 |
 * |------------|------------------------|---------------------|
 * | Control    | Home Page Feed         | Home Page Feed      |
 * | Treatment  | News Feed              | Home Page Feed      |
 *
 */
export enum ContentCardNotificationsExperimentVariants {
  CONTROL = 'control',
  TREATMENT = 'treatment',
}

export const useContentCardsNotificationsExperiment = () => {
  const showBrazeContentCards = useFlag(LaunchDarklyFlag.SHOW_BRAZE_CONTENT_CARDS_ON_HOMEPAGE);

  const { isLoading: isLoadingExperiment, variant } = useExperiments();
  const [experimentLoaded, setExperimentLoaded] = useState(false);
  const hasStartedLoading = useRef(false);

  const contentCardsExperiment = RunningExperiments.NotificationCenterTest;

  const isTreatment = variant(contentCardsExperiment, 'treatment', {
    skipExposureLogging: true,
  });

  const isControl = variant(contentCardsExperiment, 'control', {
    skipExposureLogging: true,
  });

  const isNewsFeedEnabled = useMemo(() => isMobile() && isTreatment, [isTreatment]);

  const isHomePageFeedEnabled = useMemo(() => isControl || (isTreatment && !isMobile()), [
    isControl,
    isTreatment,
  ]);

  const isExperimentEnabled = isNewsFeedEnabled || isHomePageFeedEnabled;

  useEffect(() => {
    if (isLoadingExperiment) {
      hasStartedLoading.current = true;
    } else if (hasStartedLoading.current) {
      setExperimentLoaded(true);
    }
  }, [isLoadingExperiment]);

  // Flag OFF. Always show Sanity Cards on Homepage. Don't show News section
  if (!showBrazeContentCards) {
    return {
      isNewsFeedEnabled: false,
      isHomePageFeedEnabled: false,
      isExperimentEnabled: false,
    };
  }

  // FLag ON and experiment OFF. Show Braze Content Cards on Homepage
  if (!isExperimentEnabled) {
    return {
      isNewsFeedEnabled: false,
      isHomePageFeedEnabled: true,
      isExperimentEnabled: experimentLoaded,
    };
  }

  // Flag ON and Experiment ON. Show Braze CC on homepage for Control and News Section for treatment
  return {
    isNewsFeedEnabled,
    isHomePageFeedEnabled,
    isExperimentEnabled: isExperimentEnabled && experimentLoaded,
    experimentToExpose: contentCardsExperiment,
  };
};
